/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/QaItpModal.tsx
 * FILE_ID: JOBHUB-QA-ITP-MODAL-001
 * PURPOSE: Centred ITP popup whose Export header cannot scroll away.
 */
import { useEffect, useMemo, useState } from 'react'
import { X, Pencil, Save, Trash2 } from 'lucide-react'
import { BoxResizeHandles, clampSize, QaPhotoDisplayControls, usePersistentSize } from '@quotech/ui'
import { ItpBrandLockup } from '../../qa/itpBrandLockup'
import { QaItpAreaBand, QaItpExportButton } from './QaItpView'
import { QaMergedAreas } from './useQaEvidenceUrls'
import type { ItpCoverFields } from '../../qa/itpCover'
import { checklistGroups, groupEvidenceByItem } from '../../qa/qaApiHelpers'
import type { createQaApi, QaAreaExecution, QaItemStatus } from '../../qa/qaApi'
import { QaChecklistSectionHeader } from './QaChecklistSectionHeader'
import { QaItpItemCard } from './QaItpItemCard'
import { modalExportRequest, type QaItpRequest } from './qaItpScope'

export function QaItpModal(props: Parameters<typeof QaItpModalBody>[0]) {
  return (
    <QaMergedAreas api={props.api} areas={props.areas} enabled>
      {(areas) => <QaItpModalBody {...props} areas={areas} />}
    </QaMergedAreas>
  )
}

function QaItpModalBody({
  onClose,
  areas,
  request,
  jobId,
  api,
  onMutated,
  onSetStatus,
  statusPending = false,
  onExport,
  onSaveToLibrary,
  onRenameChecklist,
  onDeleteChecklist,
  onWriteError,
  itemSelection,
  checklistSelection,
}: {
  onClose: () => void
  areas: QaAreaExecution[]
  request: QaItpRequest
  jobId: number
  jobNumber: string | number
  reportTitle?: string
  cover?: ItpCoverFields
  api: ReturnType<typeof createQaApi>
  onMutated: () => void
  onSetStatus: (itemId: string, status: QaItemStatus) => void
  statusPending?: boolean
  onExport?: (request: QaItpRequest) => void
  onSaveToLibrary?: () => void
  itemSelection?: {
    isSelected: (id: string, checklistId: string, areaId: string) => boolean
    onToggle: (id: string, checklistId: string, areaId: string) => void
  }
  checklistSelection?: { isSelected: (id: string) => boolean; onToggle: (id: string) => void }
  onRenameChecklist?: (checklistId: string, title: string) => void
  onDeleteChecklist?: (checklistId: string) => void
  onWriteError?: (message: string) => void
}) {
  const selectable = Boolean(itemSelection)
  const [frozen] = useState(() => ({
    areaIds: request.areaIds,
    checklistIds: request.checklistIds,
    itemIds: request.itemIds,
  }))
  const scope = selectable ? frozen : request
  const groups = useMemo(() => {
    const wantedLists = scope.checklistIds ? new Set(scope.checklistIds) : null
    const wantedItems = scope.itemIds ? new Set(scope.itemIds) : null
    return areas
      .filter((area) => scope.areaIds.includes(area.id))
      .flatMap((area) =>
        checklistGroups(area, undefined)
          .filter((group) => !wantedLists || wantedLists.has(group.checklist.id))
          .map((group) => {
            const items = wantedItems
              ? group.items.filter((item) => wantedItems.has(item.id))
              : group.items
            return { area, group: { ...group, items, total: items.length } }
          })
          .filter((row) => row.group.items.length > 0),
      )
  }, [areas, scope])
  const title = groups.length === 1
    ? groups[0].group.name
    : `${groups.length} checklists`
  const visibleIds = groups.flatMap((row) => row.group.items.map((item) => item.id))
  const one = groups.length === 1 ? groups[0] : null
  const [startRename, setStartRename] = useState(false)
  const [storedW, setW] = usePersistentSize('qa.itp.modal.w', 1100)
  const [storedH, setH] = usePersistentSize('qa.itp.modal.h', 720)
  const [storedX, setX] = usePersistentSize('qa.itp.modal.x', -1)
  const [storedY, setY] = usePersistentSize('qa.itp.modal.y', -1)
  const vpW = typeof window === 'undefined' ? 1200 : window.innerWidth
  const vpH = typeof window === 'undefined' ? 800 : window.innerHeight
  const bounds = { minW: 480, minH: 360, maxW: vpW - 32, maxH: vpH - 32, margin: 16, vpW, vpH }
  const w = clampSize(storedW, bounds.minW, bounds.maxW)
  const h = clampSize(storedH, bounds.minH, bounds.maxH)
  const x = clampSize(
    storedX < 0 ? (vpW - w) / 2 : storedX,
    bounds.margin,
    Math.max(bounds.margin, vpW - w - bounds.margin),
  )
  const y = clampSize(
    storedY < 0 ? (vpH - h) / 2 : storedY,
    bounds.margin,
    Math.max(bounds.margin, vpH - h - bounds.margin),
  )

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-40 bg-background/70"
      role="presentation"
      data-testid="qa-itp-modal-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="qa-itp-modal-title"
        data-testid="qa-itp-modal"
        className="fixed flex flex-col overflow-hidden rounded-xl border border-border bg-card shadow-lg"
        style={{ left: x, top: y, width: w, height: h }}
      >
        <header className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-border px-3 py-2">
          <h2 id="qa-itp-modal-title" className="min-w-0 truncate text-sm font-semibold">
            {title}
          </h2>
          <div className="flex flex-wrap items-center gap-2">
            <QaPhotoDisplayControls />
            <QaItpExportButton
              onExport={() => onExport?.(modalExportRequest(
                groups.map(({ area, group }) => ({
                  areaId: area.id,
                  checklistId: group.checklist.id,
                  itemIds: group.items.map((item) => item.id),
                })),
                itemSelection ? itemSelection.isSelected : () => false,
              ))}
              disabled={visibleIds.length === 0 && !(Array.isArray(request.itemIds) && request.itemIds.length === 0)}
            />
            {onSaveToLibrary ? (
              <button
                type="button"
                onClick={onSaveToLibrary}
                title="Save to ITP library"
                aria-label="Save to ITP library"
                className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border bg-background text-foreground hover:bg-accent"
              >
                <Save className="h-3.5 w-3.5" aria-hidden />
              </button>
            ) : null}
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1 text-muted-foreground hover:text-foreground"
              aria-label="Close ITP"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="space-y-3 p-3">
            <ItpBrandLockup />
            {groups.length === 0 ? (
              <p className="text-sm text-muted-foreground">No checks in this plan.</p>
            ) : groups.map(({ area, group }, index) => (
              <section key={`${area.id}:${group.checklist.id}`} className="space-y-2">
                {index === 0 || groups[index - 1].area.id !== area.id ? (
                  <QaItpAreaBand level={area.floor_name} area={area.area_name} />
                ) : null}
                <QaChecklistSectionHeader
                  title={group.name}
                  done={group.done}
                  total={group.total}
                  expanded
                  onToggle={() => {}}
                  selected={checklistSelection?.isSelected(group.checklist.id) ?? false}
                  onToggleSelected={checklistSelection
                    ? () => checklistSelection.onToggle(group.checklist.id)
                    : undefined}
                  onRename={onRenameChecklist
                    ? (next) => onRenameChecklist(group.checklist.id, next)
                    : undefined}
                  startRename={startRename && one?.group.checklist.id === group.checklist.id}
                  onRenameStarted={() => setStartRename(false)}
                />
                {group.items.map((item) => (
                  <QaItpItemCard
                    key={item.id}
                    item={item}
                    selected={itemSelection?.isSelected(item.id, group.checklist.id, area.id) ?? false}
                    onToggleSelected={itemSelection
                      ? () => itemSelection.onToggle(item.id, group.checklist.id, area.id)
                      : undefined}
                    evidence={groupEvidenceByItem(area.evidence ?? [])[item.id] ?? []}
                    jobId={jobId}
                    areaId={area.id}
                    api={api}
                    onMutated={onMutated}
                    onSetStatus={onSetStatus}
                    statusPending={statusPending}
                    onWriteError={onWriteError}
                  />
                ))}
              </section>
            ))}
          </div>
        </div>
        {one && (onRenameChecklist || onDeleteChecklist) && (
          <div className="flex shrink-0 flex-wrap gap-2 border-t border-border p-3">
            {onRenameChecklist && (
              <button
                type="button"
                onClick={() => setStartRename(true)}
                className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs"
              >
                <Pencil className="h-3.5 w-3.5" aria-hidden />
                Edit checklist
              </button>
            )}
            {onDeleteChecklist && (
              <button
                type="button"
                onClick={() => onDeleteChecklist(one.group.checklist.id)}
                className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-destructive"
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden />
                Delete checklist
              </button>
            )}
          </div>
        )}
        <BoxResizeHandles
          label="ITP"
          box={{ x, y, w, h }}
          bounds={bounds}
          onChange={(next) => {
            setX(next.x)
            setY(next.y)
            setW(next.w)
            setH(next.h)
          }}
          axisLabels={{ e: 'Resize ITP width', s: 'Resize ITP height' }}
        />
      </div>
    </div>
  )
}
